import { Link } from "@/lib/router-compat";
import { useSEO } from "@/hooks/useSEO";
import { ArrowLeft, Rocket, CheckCircle, Brain, Clock, Home, AlertTriangle } from "lucide-react";

import { ArticleTOC } from "@/components/resources/ArticleTOC";
import { ArticleJsonLd } from "@/components/resources/ArticleJsonLd";

const sections = [
  { id: "understanding", title: "Understanding Procrastination" },
  { id: "why-we-procrastinate", title: "Why We Procrastinate" },
  { id: "two-minute-rule", title: "The 2-Minute Rule" },
  { id: "breaking-tasks", title: "Breaking Tasks Down" },
  { id: "environment-design", title: "Environment Design" },
  { id: "accountability", title: "Accountability Strategies" },
  { id: "deeper-issues", title: "When Procrastination Signals a Problem" },
  { id: "key-takeaways", title: "Key Takeaways" },
];

const BeatingProcrastinationGuide = () => {
  useSEO({
    title: "Beating Procrastination: Science-Based Strategies | System Atlas",
    description: "Understand why you procrastinate and learn proven strategies to overcome it. From the 2-minute rule to environment design, take control of your productivity.",
    type: "article",
    alternates: [
      { hreflang: "en", href: "https://systematlas.fr/learn/beating-procrastination" },
      { hreflang: "fr", href: "https://systematlas.fr/fr/learn/beating-procrastination" },
      { hreflang: "x-default", href: "https://systematlas.fr/learn/beating-procrastination" },
    ],
  });

  return (
    <>
      <ArticleJsonLd title="Beating Procrastination: Science-Based Strategies" description="Understand why you procrastinate and learn proven strategies to overcome it." publishedDate="2026-02-08" modifiedDate="2026-02-21" inLanguage="en" />
      <div className="container mx-auto px-6 py-12 max-w-3xl">
        <Link to="/learn" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-8 text-sm"><ArrowLeft className="w-4 h-4" /> Back to Learning Guides</Link>

        <article>
          <h1 className="text-3xl md:text-4xl font-serif font-bold mb-6 tracking-tight">Beating Procrastination: A Practical Guide</h1>
          <p className="text-lg text-muted-foreground mb-8 leading-relaxed">Procrastination isn't laziness. it's an emotional regulation problem. Understanding why you procrastinate is the first step to overcoming it.</p>

          <ArticleTOC sections={sections} />

          <section id="understanding" className="mb-10">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2"><Brain className="w-5 h-5 text-primary" /> Understanding Procrastination</h2>
            <p className="text-muted-foreground mb-4 leading-relaxed">Procrastination is the act of delaying tasks despite knowing the delay will make things worse. It's not a time management problem. it's a mood management problem.</p>
            <p className="text-muted-foreground leading-relaxed">When we face a task that triggers negative emotions, our brain seeks relief by avoiding it. The short-term mood boost reinforces the habit.</p>
          </section>

          <section id="why-we-procrastinate" className="mb-10">
            <h2 className="text-xl font-semibold mb-4">Why We Procrastinate</h2>
            <ul className="space-y-3">
              {[{ l: "Task aversion:", t: "The task feels boring, difficult, or unpleasant." }, { l: "Overwhelm:", t: "The task feels too big or unclear." }, { l: "Perfectionism:", t: "Fear of not doing it well enough." }, { l: "Low self-efficacy:", t: "We doubt our ability to complete the task." }, { l: "Delayed rewards:", t: "Benefits are too far in the future." }].map(i => (
                <li key={i.l} className="flex items-start gap-3"><CheckCircle className="w-5 h-5 text-primary mt-0.5 shrink-0" /><span className="text-muted-foreground"><strong className="text-foreground">{i.l}</strong> {i.t}</span></li>
              ))}
            </ul>
          </section>

          <section id="two-minute-rule" className="mb-10">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2"><Clock className="w-5 h-5 text-primary" /> The 2-Minute Rule</h2>
            <div className="p-4 border border-primary/30 bg-primary/5 rounded-lg mb-4"><p className="font-medium text-center">"If a task takes less than 2 minutes, do it now."</p></div>
            <p className="text-muted-foreground mb-4 leading-relaxed">For larger tasks, commit to just 2 minutes. Starting is the hardest part. momentum often carries you forward.</p>
          </section>

          <section id="breaking-tasks" className="mb-10">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2"><Rocket className="w-5 h-5 text-primary" /> Breaking Tasks Down</h2>
            <div className="space-y-4">
              <div className="p-4 border border-border/60 rounded-lg"><h3 className="font-medium mb-2 text-destructive/80">❌ Too vague</h3><p className="text-muted-foreground text-sm">"Study for exam"</p></div>
              <div className="p-4 border border-border/60 rounded-lg"><h3 className="font-medium mb-2 text-primary">✓ Actionable</h3>
                <ul className="text-muted-foreground text-sm space-y-1"><li>• Review chapter 1 notes (20 min)</li><li>• Create flashcards for key terms (15 min)</li><li>• Do practice problems 1-5 (25 min)</li><li>• Test myself without notes (10 min)</li></ul>
              </div>
            </div>
          </section>

          <section id="environment-design" className="mb-10">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2"><Home className="w-5 h-5 text-primary" /> Environment Design</h2>
            <ul className="space-y-3">
              {[{ l: "Remove distractions:", t: "Phone in another room, website blockers enabled." }, { l: "Make starting easy:", t: "Leave your materials out." }, { l: "Create a dedicated space:", t: "Your brain associates locations with behaviors." }, { l: "Use cues:", t: "A specific playlist or ritual signals work time." }].map(i => (
                <li key={i.l} className="flex items-start gap-3"><CheckCircle className="w-5 h-5 text-primary mt-0.5 shrink-0" /><span className="text-muted-foreground"><strong className="text-foreground">{i.l}</strong> {i.t}</span></li>
              ))}
            </ul>
          </section>

          <section id="accountability" className="mb-10">
            <h2 className="text-xl font-semibold mb-4">Accountability Strategies</h2>
            <div className="space-y-4">
              {[{ t: "Study groups", d: "Commit to meeting others at a specific time." }, { t: "Public commitments", d: "Tell someone your goal and deadline." }, { t: "Body doubling", d: "Work alongside someone else (even virtually)." }, { t: "Scheduled check-ins", d: "Regular meetings with a mentor or study partner." }].map(i => (
                <div key={i.t} className="p-4 border border-border/60 rounded-lg"><h3 className="font-medium mb-2">{i.t}</h3><p className="text-muted-foreground text-sm leading-relaxed">{i.d}</p></div>
              ))}
            </div>
          </section>

          <section id="deeper-issues" className="mb-10">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-primary" /> When Procrastination Signals a Problem</h2>
            <ul className="space-y-3">
              {[{ l: "Wrong goal:", t: "If you consistently avoid something, ask whether you actually want to pursue it." }, { l: "Burnout:", t: "Chronic procrastination may indicate you need rest." }, { l: "Anxiety or depression:", t: "If it severely impacts your life, consider speaking with a professional." }, { l: "ADHD:", t: "Persistent difficulty with task initiation can be a symptom of attention disorders." }].map(i => (
                <li key={i.l} className="flex items-start gap-3"><CheckCircle className="w-5 h-5 text-primary mt-0.5 shrink-0" /><span className="text-muted-foreground"><strong className="text-foreground">{i.l}</strong> {i.t}</span></li>
              ))}
            </ul>
            <p className="text-muted-foreground mt-4 leading-relaxed">Be compassionate with yourself. Procrastination is a coping mechanism.</p>
          </section>

          <section id="key-takeaways" className="p-6 bg-primary/5 border border-primary/15 rounded-xl">
            <h2 className="text-lg font-medium mb-3">Key Takeaways</h2>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>• Procrastination is about managing emotions, not managing time</li>
              <li>• Use the 2-minute rule: start with just 2 minutes of work</li>
              <li>• Break vague tasks into specific, actionable steps</li>
              <li>• Design your environment to remove distractions</li>
              <li>• External accountability helps when internal motivation fails</li>
              <li>• Chronic procrastination may signal burnout or other issues</li>
              <li>• Be compassionate. shame makes procrastination worse</li>
            </ul>
          </section>
        </article>

        <nav className="mt-12 pt-8 border-t border-border/40">
          <h3 className="font-medium mb-4">Related Learning Guides</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <Link to="/learn/pomodoro-technique" className="p-4 border border-border/60 rounded-lg hover:border-primary/40 transition-colors"><h4 className="font-medium mb-1 text-sm">The Pomodoro Technique</h4><p className="text-xs text-muted-foreground">Structured work sessions to maintain focus</p></Link>
            <Link to="/learn/active-recall" className="p-4 border border-border/60 rounded-lg hover:border-primary/40 transition-colors"><h4 className="font-medium mb-1 text-sm">Active Recall</h4><p className="text-xs text-muted-foreground">Make your study time more effective</p></Link>
          </div>
        </nav>
      </div>
    </>
  );
};

export default BeatingProcrastinationGuide;
